import ApolloClient from 'apollo-client';
// Imported from the module rather than the graphql/entry barrel: entry pulls the
// whole app graph, and this file only needs one document.
import { REPORT_FILE_DOWNLOADS_PAGE } from 'graphql/search';
import { EXPORT_FETCH_CONCURRENCY, EXPORT_TRANSFORM_CHUNK_SIZE } from 'constants/index';
import { mapWithConcurrency, yieldToBrowser } from 'utils/concurrency';
import { FILE_KEYS, DOWNLOAD_DATE, USER_KEYS } from 'views/reports/fileReport/constants';

// Shared transform for the file, video and audio report exports. The three used
// to be near-identical copies of this logic, differing only in which query they
// re-ran and how they formatted the download date.

// The report search returns the first page of downloads per record at whatever
// downloadPerPage it was given. Follow-up fetches use their own, larger page.
const SEARCH_DOWNLOADS_PER_PAGE = 100;
const DOWNLOAD_FETCH_PER_PAGE = 500;

// Unit separator. Joining a row on it to hash the row cannot make two different
// rows collide, because report data does not contain control characters.
const ROW_KEY_SEPARATOR = String.fromCharCode(31);

type DownloadDateFormatter = (value: unknown) => string;

/**
 * Cache download-date formatting on the raw value. A busy file has thousands of
 * downloads and the formats are day or minute resolution, so the same string
 * comes back over and over.
 *
 * @param {DownloadDateFormatter} formatter the report's own date format
 */
export function memoizeDateFormatter(formatter: DownloadDateFormatter): DownloadDateFormatter {
  const cache = new Map<string, string>();
  return (value: unknown) => {
    if (!value) return '';
    const key = String(value);
    const cached = cache.get(key);
    if (cached !== undefined) return cached;
    const formatted = formatter(value);
    cache.set(key, formatted);
    return formatted;
  };
}

interface DownloadBatch {
  ids: string[];
  page: number;
  // Whether this request should keep asking for the next page while responses come
  // back exactly full. Set on the last page planned for a set of ids, so an
  // understated count cannot silently truncate the history -- and never on an
  // earlier page, which would refetch what a later batch already covers.
  drain: boolean;
}

/**
 * Plan the follow-up download requests.
 *
 * Records whose downloads all arrived with the search need nothing. The rest are
 * bin-packed by download count so that one request covers as many records as will
 * fit in a page, because Query.fileDownloads takes a list of fileIds rather than
 * one. A record with more downloads than fit in a single page gets a batch of its
 * own per page.
 *
 * This is the difference between one request per record per hundred downloads and
 * one request per five hundred downloads: on a report where thousands of records
 * each have a few hundred downloads, that is thousands of round trips against
 * dozens.
 *
 * @param {any[]} recordData accumulated export rows
 * @returns {DownloadBatch[]} the requests to make
 */
export function planDownloadBatches(recordData: any[]): DownloadBatch[] {
  const batches: DownloadBatch[] = [];

  let group: string[] = [];
  let groupCount = 0;

  const flush = () => {
    if (group.length === 0) return;
    batches.push({ ids: group, page: 1, drain: true });
    group = [];
    groupCount = 0;
  };

  for (let i = 0; i < recordData.length; i += 1) {
    const item = recordData[i]?.item;
    const count = item?._fileDownloadStats?.count ?? 0;
    // The search already returned everything this record has.
    if (!item?.id || count <= SEARCH_DOWNLOADS_PER_PAGE) continue;

    if (count > DOWNLOAD_FETCH_PER_PAGE) {
      // Too big to share a request: page it on its own.
      flush();
      const pages = Math.ceil(count / DOWNLOAD_FETCH_PER_PAGE);
      for (let page = 1; page <= pages; page += 1) {
        batches.push({ ids: [item.id], page, drain: page === pages });
      }
      continue;
    }

    if (groupCount + count > DOWNLOAD_FETCH_PER_PAGE) flush();
    group.push(item.id);
    groupCount += count;
  }
  flush();

  return batches;
}

/**
 * Fetch the full download history for every record that has more than the search
 * returned, keyed by record id.
 *
 * Requests are targeted at the downloads themselves rather than re-running the
 * whole paged report query -- which fetched every record on the page, all of their
 * downloads and every download's user, to read one index out of the result. That
 * index was also wrong past the first page: it was the record's position in the
 * full accumulated set applied to a fresh page-one result, so any record after the
 * hundredth read undefined and had its downloads dropped by the try/catch.
 *
 * Batches are re-paged when a response comes back exactly full, since that means
 * the planned page size may have truncated it.
 *
 * @param {ApolloClient} client apollo client
 * @param {any[]} recordData accumulated export rows
 * @param {unknown} filterString the report's download filter
 * @param {Function} onProgress called as batches complete
 */
export async function fetchAllDownloads(
  client: ApolloClient<unknown>,
  recordData: any[],
  filterString: unknown,
  onProgress?: (completed: number, total: number) => void,
): Promise<Map<string, unknown[]>> {
  const byId = new Map<string, unknown[]>();
  const batches = planDownloadBatches(recordData);

  onProgress?.(0, batches.length);
  if (batches.length === 0) return byId;

  // Every record in the plan is refetched in full, so nothing is seeded from the
  // search result -- that avoids having to reconcile two different orderings.
  for (let i = 0; i < batches.length; i += 1) {
    const ids = batches[i].ids;
    for (let j = 0; j < ids.length; j += 1) if (!byId.has(ids[j])) byId.set(ids[j], []);
  }

  let completed = 0;

  async function fetchBatch(ids: string[], page: number): Promise<unknown[]> {
    const { data } = await client.query({
      query: REPORT_FILE_DOWNLOADS_PAGE,
      variables: { fileIds: ids, filterString, perPage: DOWNLOAD_FETCH_PER_PAGE, page },
      fetchPolicy: 'no-cache',
    });
    return (data?.fileDownloads ?? []) as unknown[];
  }

  const results = await mapWithConcurrency(batches, EXPORT_FETCH_CONCURRENCY, async ({ ids, page, drain }) => {
    const downloads: unknown[] = [];
    try {
      let current = page;
      while (true) {
        const batch = await fetchBatch(ids, current);
        for (let i = 0; i < batch.length; i += 1) downloads.push(batch[i]);
        // Only the last planned page keeps going, and only while responses arrive
        // exactly full. Any earlier page stops after one request, because the pages
        // after it are batches of their own.
        if (!drain || batch.length < DOWNLOAD_FETCH_PER_PAGE) break;
        current += 1;
      }
    } catch (err) {
      // Matches the previous behaviour: a failed download page drops those rows
      // rather than failing the whole export.
      console.log(err);
    }

    completed += 1;
    onProgress?.(completed, batches.length);
    return downloads;
  });

  for (let i = 0; i < results.length; i += 1) {
    const downloads = results[i] || [];
    for (let j = 0; j < downloads.length; j += 1) {
      const download = downloads[j] as any;
      const fileId = download?.file?.id;
      const bucket = fileId && byId.get(fileId);
      if (bucket) bucket.push(download);
    }
  }

  return byId;
}

/**
 * Build one CSV row for a record, for one of its downloads when there is one.
 *
 * @param {any} row the file, video or audio record
 * @param {any} download the download this row is for, if any
 * @param {DownloadDateFormatter} formatDownloadDate report-specific date format
 */
export function buildReportRow(row: any, download: any, formatDownloadDate: DownloadDateFormatter): string[] {
  const fileData = FILE_KEYS.map((key: string) => {
    if (key === 'absoluteUrl') {
      const url = row[key] && String(row[key]);
      if (!url) return '';
      return url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('?')) || '';
    }
    return (row && row[key]) ? row[key] : '';
  });

  const downloadDate = DOWNLOAD_DATE.map((key: string) => {
    if (key === 'taxonomies') {
      return row[key]?.map((item) => item.title).join(", ");
    }
    // Guarded here rather than left to the formatter, so the column is blank for a
    // record with no downloads whatever formatter the report supplies.
    return download?.downloadedAt ? formatDownloadDate(download.downloadedAt) : '';
  });

  const user = download?.user;
  const userData = USER_KEYS.map((key: string) => (user && user[key]) ? user[key] : '');

  return [...fileData, ...downloadDate, ...userData];
}

/**
 * Turn the accumulated report records into CSV rows, one per download, yielding
 * to the browser between chunks so a large report keeps the tab responsive.
 *
 * @param {any[]} recordData accumulated export rows
 * @param {Map<string, unknown[]>} downloadsById full download lists by record id
 * @param {DownloadDateFormatter} formatDownloadDate report-specific date format
 */
export async function buildReportRows(
  recordData: any[],
  downloadsById: Map<string, unknown[]>,
  formatDownloadDate: DownloadDateFormatter,
): Promise<string[][]> {
  const rows: string[][] = [];

  for (let i = 0; i < recordData.length; i += 1) {
    const item = recordData[i]?.item;
    if (!item) continue;

    // Fall back to what the search returned if a batch failed and left an empty
    // bucket -- better a partial history than a record with none.
    const fetched = downloadsById.get(item.id);
    const downloads = (fetched && fetched.length > 0) ? fetched : (item.fileDownloads || []);
    if (downloads.length > 0) {
      for (let j = 0; j < downloads.length; j += 1) {
        rows.push(buildReportRow(item, downloads[j], formatDownloadDate));
      }
    } else {
      rows.push(buildReportRow(item, undefined, formatDownloadDate));
    }

    if ((i + 1) % EXPORT_TRANSFORM_CHUNK_SIZE === 0) await yieldToBrowser();
  }

  return rows;
}

/**
 * Drop repeated rows, keeping the first occurrence and the original order.
 *
 * The file report deduped by scanning everything kept so far for each row and
 * comparing it cell by cell -- O(rows^2 x columns), the slowest thing in that
 * export. It also did not fully dedupe: when a row matched the row after it, that
 * row was kept unconditionally without checking what had already been kept, so a
 * value seen earlier came through twice. Fuzzed against this implementation over
 * 200k random inputs, the old one emitted duplicates in ~21% of cases. Hashing
 * each row once is both linear and actually exhaustive.
 *
 * @param {string[][]} rows CSV rows
 */
export function dedupeRows(rows: string[][]): string[][] {
  const seen = new Set<string>();
  const unique: string[][] = [];

  for (let i = 0; i < rows.length; i += 1) {
    const key = rows[i].join(ROW_KEY_SEPARATOR);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(rows[i]);
  }

  return unique;
}
